// src/components/item-builder/ItemQuickCreateModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

// مدال‌های هر نوع سوال
import McqSingleModal from "./McqSingleModal";
import TrueFalseModal from "./TrueFalseModal";
import McqMultiModal from "./McqMultiModal";
import ShortTextModal from "./ShortTextModal";
import NumericModal from "./NumericModal";
import MatchingModal from "./MatchingModal";
import OrderingModal from "./OrderingModal";
import OrderingWeightedModal from "./OrderingWeightedModal";
import EssayModal from "./EssayModal";
import FileUploadModal from "./FileUploadModal";
import McqMatrixModal from "./McqMatrixModal";
import ClozeRegexModal from "./ClozeRegexModal";
import WordBankModal from "./WordBankModal";
import DragToGroupModal from "./DragToGroupModal";
import PassageMcqModal from "./PassageMcqModal";
import ImageMatchingModal from "./ImageMatchingModal";
import WritingPromptModal from "./WritingPromptModal";
import DragDropItemTargetModal from "./DragDropItemTargetModal";
import SelectSentenceModal from "./SelectSentenceModal";
import AudioPromptModal from "./AudioPromptModal";
import SentenceBuilderModal from "./SentenceBuilderModal";
import ClozeModal from "./ClozeTextModal";
import AudioGateModal from "./AudioGateModal";
import SpeakingIntegratedModal from "./SpeakingIntegratedModal";
import WordOrderingModal from "./WordOrderingModal";
import CtestModal from "./CtestModal";
import { CDN_BASE_URL } from "../../config";

// map نوع سوال → کامپوننت مدالش
const MODAL_COMPONENTS = {
  // MCQ
  mcq_single: McqSingleModal,
  mcq_multi: McqMultiModal,
  mcq_matrix: McqMatrixModal,
  true_false: TrueFalseModal,

  // Matching / Grouping
  matching: MatchingModal,
  drag_to_group: DragToGroupModal,
  image_matching: ImageMatchingModal,

  // Ordering
  ordering: OrderingModal,
  ordering_weighted: OrderingWeightedModal,

  // Cloze
  cloze_text: ClozeModal,
  cloze_regex: ClozeRegexModal,
  word_bank: WordBankModal,

  // Short/Essay/Numeric
  short_text: ShortTextModal,
  essay: EssayModal,
  numeric: NumericModal,

  // Drag & Drop
  drag_drop_item_target: DragDropItemTargetModal,
  sentence_builder: SentenceBuilderModal,

  // Reading
  select_sentence: SelectSentenceModal,
  passage_mcq: PassageMcqModal,

  // Media / Speaking & Listening
  audio_prompt: AudioPromptModal,
  audio_gate: AudioGateModal,
  speaking_integrated: SpeakingIntegratedModal,

  // Writing
  writing_prompt: WritingPromptModal,

  // Other
  file_upload: FileUploadModal,
  c_test: CtestModal,
  sentence_reconstruction: WordOrderingModal,
};

// همه‌ی انواعی که در dropdown می‌خوای نشان بدهی
const ALL_KINDS = [
  "mcq_single",
  "mcq_multi",
  "mcq_matrix",
  "true_false",
  "matching",
  "drag_to_group",
  "image_matching",
  "ordering",
  "ordering_weighted",
  "cloze_text",
  "cloze_regex",
  "word_bank",
  "short_text",
  "essay",
  "numeric",
  "drag_drop_item_target",
  "sentence_builder",
  "select_sentence",
  "passage_mcq",
  "audio_prompt",
  "writing_prompt",
  "file_upload",
  "audio_gate",
  "speaking_integrated",
  "c_test",
  "sentence_reconstruction",
];

const IMPLEMENTED_KINDS = new Set(Object.keys(MODAL_COMPONENTS));

export default function ItemQuickCreateModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [selectedKind, setSelectedKind] = useState(item?.kind || "mcq_single");

  // === ویدیو گاید ===
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const guideSrc = useMemo(() => `${CDN_BASE_URL}public/${selectedKind}.mp4`, [selectedKind]);

  useEffect(() => {
    if (isOpen) {
      setSelectedKind(item?.kind || "mcq_single");
      setIsGuideOpen(false);
      setVideoError(false);
    }
  }, [isOpen, item]);

  // وقتی نوع سوال عوض شد، اگر مدال ویدیو باز بود، خطا ریست شود تا ویدیو جدید لود شود
  useEffect(() => {
    if (isGuideOpen) setVideoError(false);
  }, [selectedKind, isGuideOpen]);

  if (!isOpen) return null;

  const ActiveModal = MODAL_COMPONENTS[selectedKind];

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-5xl rounded-2xl shadow-xl p-4 md:p-6 max-h-[80vh] overflow-x-scroll"
      >
        <div className="flex items-center justify-between mb-4 gap-3">
          <h2 className="text-lg md:text-xl font-semibold">
            {isEdit
              ? t("item_quick.edit_item", "ویرایش سؤال")
              : t("item_quick.create_item")}
          </h2>

          <div className="flex items-center gap-2">
            {/* دکمه راهنمای ویدیویی */}
            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="px-3 py-2 text-sm rounded-lg bg-purple-500 text-white border hover:bg-purple-600"
            >
              {t("item_quick.video_guide", "راهنمای ویدیویی")}
            </button>

            <button
              className="p-2 rounded-lg hover:bg-gray-100"
              onClick={onClose}
              aria-label="close"
            >
              <X />
            </button>
          </div>
        </div>

        {/* انتخاب نوع سوال */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            {t("item_quick.select_question_type")}
          </label>
          <select
            value={selectedKind}
            onChange={(e) => setSelectedKind(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
            disabled={isEdit}
          >
            {ALL_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`item_kinds.${k}`)}
              </option>
            ))}
          </select>
          {isEdit && (
            <p className="mt-1 text-xs text-gray-500">
              {t(
                "item_quick.kind_locked",
                "نوع سؤال در حالت ویرایش قابل تغییر نیست.",
              )}
            </p>
          )}
        </div>

        {/* رندر داینامیک مدال بر اساس kind */}
        {ActiveModal && IMPLEMENTED_KINDS.has(selectedKind) ? (
          <ActiveModal
            isOpen={isOpen}
            onClose={onClose}
            token={token}
            onCreated={onCreated}
            mode={mode}
            item={item}
          />
        ) : (
          <div className="p-4 rounded-lg border bg-gray-50 text-sm text-gray-700">
            {t("item_quick.form_not_ready")}
          </div>
        )}

        {/* === Video Guide Modal === */}
        {isGuideOpen && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
            onClick={() => setIsGuideOpen(false)}
          >
            <div
              className="bg-white w-full max-w-4xl rounded-2xl shadow-xl p-4 md:p-6"
              onClick={(e) => e.stopPropagation()}
              dir={isRtl ? "rtl" : "ltr"}
            >
              <div className="flex items-center justify-between mb-3 gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold">
                    {t("item_quick.video_guide", "راهنمای ویدیویی")}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t(`item_kinds.${selectedKind}`)} • {guideSrc}
                  </div>
                </div>

                <button
                  className="p-2 rounded-lg hover:bg-gray-100"
                  onClick={() => setIsGuideOpen(false)}
                  aria-label="close video"
                >
                  <X />
                </button>
              </div>

              <div className="rounded-xl overflow-hidden bg-black">
                {!videoError ? (
                  <video
                    key={guideSrc} // مهم: باعث میشه با تغییر kind ویدیو ری‌لود شه
                    controls
                    className="w-full h-auto"
                    onError={() => setVideoError(true)}
                  >
                    <source src={guideSrc} type="video/mp4" />
                    {/* fallback */}
                    {t(
                      "item_quick.video_not_supported",
                      "مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.",
                    )}
                  </video>
                ) : (
                  <div className="p-4 text-sm text-white">
                    {t(
                      "item_quick.video_not_found",
                      "ویدیوی راهنما برای این نوع سؤال پیدا نشد.",
                    )}
                    <div className="mt-2 text-xs text-white/80">
                      {t("item_quick.expected_path", "مسیر مورد انتظار")}:{" "}
                      {guideSrc}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                  onClick={() => setIsGuideOpen(false)}
                >
                  {t("common.close", "بستن")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
